import EditorModule from "../EditorModule";
import "./style.scss";
import SpellcheckerPanel from "./SpellcheckerPanel";
import { EMPTY_SPELLCHECK_RESULT, spellcheck, } from "./spellchecker";
import i18next from "i18next";
import { ExfluencyLoader } from "editorNextGen/components/loader";
import { spellchecked, spellcheckError, spellcheckFinished, spellcheckStarted, } from "api-operations/metrics/spellcheckerMetrics";
import { v4 as uuidv4 } from "uuid";
export default class SpellcheckerModule extends EditorModule {
    constructor(editor, contentEditionModule) {
        super(editor);
        this.moduleId = "SpellcheckerModule";
        this.spellcheckerPanel = new SpellcheckerPanel();
        editor.leftSidePanel.appendChild(this.spellcheckerPanel);
        contentEditionModule.completitionPreconditions.push(this.spellcheckText.bind(this));
    }
    unload() {
        this.spellcheckerPanel.remove();
    }
    assetActivationChanged(asset, active) {
        active && this.updateSpellchecker(asset);
    }
    assetRefreshed(asset) {
        asset.active && this.updateSpellchecker(asset);
    }
    assetCompletionChanged(asset, completed, onCommitCompleted) {
        // Clear spellcheck results when asset is completed
        if (completed) {
            this.updateSpellchecker(asset);
        }
    }
    assetChanged(asset) {
        // Clear spellcheck results when content is edited
        this.updateSpellchecker(asset);
    }
    updateButtonForSpellcheck(button, isSpellchecking, noChanges) {
        if (isSpellchecking) {
            button.classList.add("spellchecking");
            button.disabled = true;
            const loader = document.createElement("div");
            loader.className = "loader";
            loader.appendChild(new ExfluencyLoader(false, "transparent", "20px", "20px"));
            button.innerHTML = "";
            button.appendChild(loader);
            button.appendChild(document.createTextNode("AI Processing"));
        }
        else {
            button.classList.remove("spellchecking");
            button.disabled = !noChanges;
            button.textContent = i18next.t("editor.complete");
        }
    }
    async spellcheckText(assetElement) {
        const doneButton = assetElement.querySelector("button.done");
        if (doneButton) {
            this.updateButtonForSpellcheck(doneButton, true);
        }
        // Show the spellchecker panel
        this.showSpellcheckerPanel();
        const request = {
            source_text: assetElement.sourceText || "",
            target_text: assetElement.currentText || "",
            source_lang: this.editor.sourceLanguage,
            target_lang: this.editor.targetLanguage,
            requestId: uuidv4(),
        };
        spellcheckStarted(this.editor.assignmentId, assetElement.assetId, request);
        const onError = (error) => {
            spellcheckError(request.requestId, error);
        };
        const noChange = (result) => result.changes.length === 0 ||
            isPlainTextEqual(result.spellchecked_text, assetElement.currentText);
        const result = await spellcheck(request)
            .then((result) => {
            spellchecked(request.requestId, result, noChange(result));
            return result;
        })
            .catch((error) => {
            onError(error.message);
            return EMPTY_SPELLCHECK_RESULT;
        });
        const noChanges = noChange(result);
        if (!noChanges) {
            // Update the spellchecker panel
            this.updateSpellchecker(assetElement, request, {
                spellchecked_text: result.spellchecked_text,
                changes: result.changes.filter((change) => !isPlainTextEqual(change.previous_fragment, change.new_fragment)),
            });
        }
        if (doneButton) {
            this.updateButtonForSpellcheck(doneButton, false, noChanges);
        }
        return noChanges;
    }
    updateSpellchecker(asset, request, result = EMPTY_SPELLCHECK_RESULT) {
        this.spellcheckerPanel.currentText = asset.currentText;
        this.spellcheckerPanel.changes = result.changes;
        // Set up callbacks for the spellchecker panel
        this.spellcheckerPanel.onApplyAndComplete = (changes) => {
            // Apply changes based on user decisions
            const updatedText = this.applySpellcheckChanges(asset.currentText, changes);
            // Update the asset text with the processed version
            asset.currentText = updatedText;
            // Complete the asset
            asset.complete();
            request &&
                spellcheckFinished(request.requestId, changes, updatedText);
            const doneButton = asset.querySelector("button.done");
            doneButton.disabled = false;
        };
        // Add/remove visual indicator for spellcheck suggestions
        if (result.changes && result.changes.length > 0) {
            asset.setAttribute("has-spellcheck-suggestions", "");
        }
        else {
            asset.removeAttribute("has-spellcheck-suggestions");
        }
    }
    showSpellcheckerPanel() {
        this.spellcheckerPanel.style.display = "";
        this.spellcheckerPanel.showLoader();
    }
    /**
     * Applies spellcheck changes to the given text based on user decisions. Applies changes that are accepted or undefined.
     * @param originalText - The original text to apply changes to
     * @param changes - Array of spellcheck changes with user decisions
     * @returns The updated text with applied changes
     */
    applySpellcheckChanges(originalText, changes) {
        let updatedText = originalText;
        // Sort changes by start position in reverse order to avoid position shifts
        const sortedChanges = [...changes].sort((a, b) => b.start_pos - a.start_pos); // Reverse sort to process from end to start
        for (const change of sortedChanges) {
            if (change.accepted === true || change.accepted === undefined) {
                // Apply the correction
                updatedText =
                    updatedText.substring(0, change.start_pos) +
                        change.new_fragment +
                        updatedText.substring(change.end_pos);
            }
            else if (change.accepted === false) {
                // Revert to original text (keep the previous_fragment)
                updatedText =
                    updatedText.substring(0, change.start_pos) +
                        change.previous_fragment +
                        updatedText.substring(change.end_pos);
            }
            // If accepted is undefined, skip the change
        }
        return updatedText;
    }
}
// it may happen that spellckecker returns changes, but for example only in formatting
const isPlainTextEqual = (text1, text2) => {
    const tmp1 = document.createElement("div");
    tmp1.innerHTML = replaceUniversalTag(text1);
    const tmp2 = document.createElement("div");
    tmp2.innerHTML = replaceUniversalTag(text2);
    return tmp1.textContent === tmp2.textContent;
};
// replace universal-tag with span to avoid universal-tag construction
export const replaceUniversalTag = (text) => {
    return text.replace(/universal-tag/g, "span");
};
